"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { track } from "@/shared/lib/analytics"
import { getPostHogFeatureFlagClient } from "@/shared/lib/monitoring/telemetry-config"

const FLAG_KEY = "pricing-default-billing-period"

type Surface = "pricing_page" | "subscription_page" | "paywall_modal"
type Variant = "control" | "monthly_default" | "unassigned"

interface PostHogWithFlags {
  getFeatureFlag: (key: string) => string | boolean | undefined
  onFeatureFlags: (cb: () => void) => void
}

function getPostHog(): PostHogWithFlags | null {
  return getPostHogFeatureFlagClient<PostHogWithFlags>()
}

function variantToYearly(variant: string | boolean | undefined): boolean {
  return variant !== "monthly_default"
}

function normalizeVariant(variant: string | boolean | undefined): Variant {
  if (variant === "monthly_default") return "monthly_default"
  if (variant === "control") return "control"
  return "unassigned"
}

/**
 * PostHog A/B test for the pricing toggle default (yearly vs monthly).
 *
 * Variants (flag: pricing-default-billing-period):
 *   - control          → isYearly default = true   (current behaviour)
 *   - monthly_default  → isYearly default = false
 *
 * Returns `{ isYearly, setIsYearly, loaded }`. Initial value is `true` (matches
 * control) to avoid a visible flip for users who land on a cached experiment
 * assignment. When the flag resolves, the default is updated unless the user
 * has already interacted with the toggle — user choice always wins.
 *
 * Reading the flag implicitly fires `$feature_flag_called` (PostHog SDK),
 * which is the exposure event the experiment stats engine uses. We also emit
 * a typed `sub:pricing_exposure` event once per mount so results can be
 * segmented by surface.
 */
export function useBillingPeriodExperiment(surface: Surface): {
  isYearly: boolean
  setIsYearly: (value: boolean) => void
  loaded: boolean
} {
  const [isYearly, setIsYearlyState] = useState<boolean>(true)
  const [loaded, setLoaded] = useState<boolean>(false)
  const userInteractedRef = useRef<boolean>(false)
  const exposedRef = useRef<boolean>(false)

  const setIsYearly = useCallback((value: boolean) => {
    userInteractedRef.current = true
    setIsYearlyState(value)
  }, [])

  useEffect(() => {
    const ph = getPostHog()
    if (!ph) {
      setLoaded(true)
      return
    }

    const apply = () => {
      const raw = ph.getFeatureFlag(FLAG_KEY)
      const variant = normalizeVariant(raw)

      if (!userInteractedRef.current) {
        setIsYearlyState(variantToYearly(raw))
      }
      setLoaded(true)

      if (!exposedRef.current && variant !== "unassigned") {
        exposedRef.current = true
        track("sub:pricing_exposure", {
          surface,
          variant,
          default_is_yearly: variantToYearly(raw),
        })
      }
    }

    apply()
    ph.onFeatureFlags(apply)
  }, [surface])

  return { isYearly, setIsYearly, loaded }
}
